import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, Building2, Plus, Edit, Trash2, Check, X, Mail, Download, Search, Upload,
  Users, FileSpreadsheet, AlertCircle, CheckCircle, BookOpen, GraduationCap, IndianRupee,
  PlayCircle, ClipboardList, Bell, ChevronDown, LogOut, Settings, BarChart3, TrendingUp,
  TrendingDown, Briefcase, Target, Zap, ArrowRight, Eye, MoreVertical, MapPin, Activity,
  Wallet, CreditCard, BadgeCheck, Trophy, Award, Globe, Phone, Calendar, RefreshCw,
  PieChart, Filter, ChevronRight, ExternalLink, Loader2, XCircle, Clock, Send, FileText,
  Building, UserPlus, DollarSign, ArrowUpRight, ArrowDownRight, Sparkles
} from 'lucide-react';
import FeeManagement from './FeeManagement';
import LMSManagement from './LMSManagement';
import ExamManagement from './ExamManagement';
import RoleManagement from './RoleManagement';
import SubAdminManagement from './SubAdminManagement';
import ActivityLogs from './ActivityLogs';
import DataHealth from './DataHealth';
import AdminDashboardWrapper from '@/components/admin/AdminDashboardWrapper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PARTNER_FEES = {
  SP: 500000,
  DLC: 150000,
  TC: 50000
};

// Indian States List for dropdown
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh"
];

const AdminDashboard = () => {
  const { user, isAuthenticated, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const tabsSectionRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  
  // UI States
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setShowProfileMenu(false);
        setShowNotifications(false);
      }
    };

    if (showProfileMenu || showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showProfileMenu, showNotifications]);

  // Function to change tab and scroll to it
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    setTimeout(() => {
      tabsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };
  
  // Sub-Admin & RBAC States
  const [isSubAdmin, setIsSubAdmin] = useState(false);
  const [subAdminPermissions, setSubAdminPermissions] = useState({});
  const [subAdminInfo, setSubAdminInfo] = useState(null);

  // Data States
  const [certificates, setCertificates] = useState([]);
  const [franchiseApps, setFranchiseApps] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [franchiseCenters, setFranchiseCenters] = useState([]);
  const [commissionReport, setCommissionReport] = useState(null);
  const [courses, setCourses] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [placements, setPlacements] = useState([]);
  
  // Dialog States
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  
  // Add Partner Dialog State
  const [addPartnerDialogOpen, setAddPartnerDialogOpen] = useState(false);
  const [partnerDistricts, setPartnerDistricts] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [partnerForm, setPartnerForm] = useState({
    partner_type: 'TC',  // SP, DLC, TC
    center_name: '',
    owner_name: '',
    email: '',
    mobile: '',
    state: '',
    district: '',
    city: '',
    pin_code: '',
    address: '',
    password: ''
  });
  const [creatingPartner, setCreatingPartner] = useState(false);
  
  // Edit/Delete States
  const [editCenterDialogOpen, setEditCenterDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // 'center' or 'user'
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Form States
  const [courseForm, setCourseForm] = useState({
    name: '', description: '', duration: '', fee: '', course_type: 'paid',
    delivery_mode: 'offline', category: 'professional', is_active: true, eligibility: ''
  });
  const [certFormData, setCertFormData] = useState({
    name: '', dob: '', cert_number: '', status: 'valid', course: '', issue_date: '', expiry_date: ''
  });

  // Real-time activity feed
  const [activityFeed, setActivityFeed] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Icon mapping for activity feed
  const iconMap = {
    UserPlus: UserPlus,
    Building2: Building2,
    IndianRupee: IndianRupee,
    Briefcase: Briefcase,
    Trophy: Trophy,
    FileText: FileText,
    Award: Award
  };

  // WebSocket connection for real-time updates
  const connectWebSocket = () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/admin?token=${token}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected for admin dashboard');
      // Send ping every 25 seconds to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 25000);
      ws.pingInterval = pingInterval;
    };
    
    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'activity') {
          // Add new activity to the top of the feed
          setActivityFeed(prev => {
            const newActivity = {
              id: `ws_${Date.now()}`,
              type: data.activity_type,
              message: data.message,
              time: data.time,
              icon: data.activity_type === 'student_registration' ? 'UserPlus' :
                    data.activity_type === 'center_approval' ? 'Building2' :
                    data.activity_type === 'fee_payment' ? 'IndianRupee' :
                    data.activity_type === 'job_posting' ? 'Briefcase' : 'Trophy',
              color: data.activity_type === 'student_registration' ? 'blue' :
                     data.activity_type === 'center_approval' ? 'emerald' :
                     data.activity_type === 'fee_payment' ? 'green' :
                     data.activity_type === 'job_posting' ? 'purple' : 'amber'
            };
            return [newActivity, ...prev.slice(0, 19)]; // Keep max 20 items
          });
          toast.info(data.message, { duration: 3000 });
        }
      } catch (e) {
        console.error('WebSocket message parse error:', e);
      }
    };
    
    ws.onclose = () => {
      console.log('WebSocket disconnected');
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      // Reconnect after 5 seconds
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current = ws;
  };

  // Fetch real-time activity feed
  const fetchActivityFeed = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/activity/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityFeed(res.data || []);
    } catch (error) {
      console.error('Failed to fetch activity feed:', error);
      // Fallback to static data if API fails
      setActivityFeed([
        { id: 1, type: 'student', message: 'New student registered', time: 'Recently', icon: 'UserPlus', color: 'blue' },
        { id: 2, type: 'center', message: 'New center activity', time: 'Recently', icon: 'Building2', color: 'emerald' }
      ]);
    } finally {
      setActivityLoading(false);
    }
  };

  // Mock alerts
  const alerts = [
    ...(franchiseApps.filter(a => a.status === 'pending').length > 0 
      ? [{ type: 'warning', message: `${franchiseApps.filter(a => a.status === 'pending').length} franchise applications pending`, icon: Building2 }] : []),
    ...(commissionReport?.pending_amount > 0 
      ? [{ type: 'danger', message: `₹${(commissionReport.pending_amount / 1000).toFixed(0)}K commission pending from centers`, icon: CreditCard }] : []),
  ];

  // Mock notifications
  const notifications = [
    { id: 1, message: 'New franchise application from Mumbai', time: '10 min ago' },
    { id: 2, message: '5 students completed exams today', time: '1 hr ago' },
    { id: 3, message: 'Monthly revenue target achieved!', time: '3 hrs ago' }
  ];

  useEffect(() => {
    // Check for Sub-Admin login first
    const subAdminInfoStr = localStorage.getItem('sub_admin_info');
    if (subAdminInfoStr) {
      try {
        const saInfo = JSON.parse(subAdminInfoStr);
        setIsSubAdmin(true);
        setSubAdminInfo(saInfo);
        setSubAdminPermissions(saInfo.permissions || {});
        // Sub-admin can access dashboard - setup and return early
        fetchData();
        fetchActivityFeed();
        connectWebSocket();
        
        const activityInterval = setInterval(fetchActivityFeed, 30000);
        // Return cleanup function - this completes the useEffect for sub-admin
        return () => {
          clearInterval(activityInterval);
          if (wsRef.current) wsRef.current.close();
          if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        };
      } catch (e) {
        console.error('Error parsing sub_admin_info:', e);
        // If parse fails, fall through to normal auth check
      }
    }
    
    // Check for Super Admin login (only reached if not sub-admin)
    if (!isAuthenticated || user?.role !== 'admin') {
      toast.error('Access denied. Admin only.');
      navigate('/auth/login');
      return;
    }
    
    // Super Admin flow
    setIsSubAdmin(false);
    fetchData();
    fetchActivityFeed();
    connectWebSocket();
    
    // Auto-refresh activity feed every 30 seconds (fallback if WebSocket fails)
    const activityInterval = setInterval(fetchActivityFeed, 30000);
    
    return () => {
      clearInterval(activityInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isAuthenticated, user, navigate]);

  // Fetch districts when partner state changes
  useEffect(() => {
    if (partnerForm.state) {
      fetchPartnerDistricts(partnerForm.state);
    } else {
      setPartnerDistricts([]);
    }
  }, [partnerForm.state]);

  // Fetch districts for partner form
  const fetchPartnerDistricts = async (state) => {
    setLoadingDistricts(true);
    try {
      const response = await axios.get(`${API}/districts/${encodeURIComponent(state)}`);
      setPartnerDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setPartnerDistricts([]);
    }
    setLoadingDistricts(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [certsRes, appsRes, usersRes, centersRes, commissionRes, coursesRes] = await Promise.all([
        axios.get(`${API}/admin/certificates`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/franchise-applications`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/franchise-centers`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/commission-report`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/courses`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setCertificates(certsRes.data || []);
      setFranchiseApps(appsRes.data || []);
      setAllUsers(usersRes.data || []);
      setFranchiseCenters(centersRes.data || []);
      setCommissionReport(commissionRes.data);
      setCourses(coursesRes.data || []);

      // Fetch employers and jobs
      try {
        const [employersRes, jobsRes] = await Promise.all([
          axios.get(`${API}/admin/employers`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/jobs?limit=100`, { headers }).catch(() => ({ data: { jobs: [] } }))
        ]);
        setEmployers(employersRes.data || []);
        setJobs(jobsRes.data?.jobs || []);
      } catch (e) {}
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Don't show toast error for Sub-Admin as some APIs may be restricted
      if (!isSubAdmin) {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Global search handler
  const handleGlobalSearch = (term) => {
    setGlobalSearch(term);
    if (term.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    const results = [];
    const lowerTerm = term.toLowerCase();
    
    // Search students
    allUsers.filter(u => u.role === 'student').forEach(u => {
      if (u.name?.toLowerCase().includes(lowerTerm) || u.email?.toLowerCase().includes(lowerTerm)) {
        results.push({ type: 'Student', name: u.name, email: u.email, id: u.id });
      }
    });
    
    // Search centers
    franchiseCenters.forEach(c => {
      if (c.center_name?.toLowerCase().includes(lowerTerm) || c.center_code?.toLowerCase().includes(lowerTerm)) {
        results.push({ type: 'Center', name: c.center_name, code: c.center_code, id: c.id });
      }
    });
    
    // Search employers
    allUsers.filter(u => u.role === 'employer').forEach(u => {
      if (u.name?.toLowerCase().includes(lowerTerm) || u.company_name?.toLowerCase().includes(lowerTerm)) {
        results.push({ type: 'Employer', name: u.name || u.company_name, email: u.email, id: u.id });
      }
    });
    
    setSearchResults(results.slice(0, 10));
    setShowSearchResults(true);
  };

  const handleLogout = () => {
    // Clear sub-admin data if present
    localStorage.removeItem('sub_admin_info');
    logout();
    navigate('/auth/login');
  };

  // Create Partner (Training Center / DLC / State Partner)
  const handleCreatePartner = async () => {
    if (!partnerForm.center_name || !partnerForm.owner_name || !partnerForm.email || !partnerForm.mobile || !partnerForm.password) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (partnerForm.partner_type === 'SP' && !partnerForm.state) {
      toast.error('State is required for State Partner');
      return;
    }
    
    if (partnerForm.partner_type === 'DLC' && (!partnerForm.state || !partnerForm.district)) {
      toast.error('State and District are required for DLC');
      return;
    }
    
    setCreatingPartner(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create partner directly (bypassing application flow)
      const response = await axios.post(`${API}/admin/create-partner`, {
        partner_type: partnerForm.partner_type,
        center_name: partnerForm.center_name,
        owner_name: partnerForm.owner_name,
        email: partnerForm.email,
        mobile: partnerForm.mobile,
        state: partnerForm.state,
        district: partnerForm.district,
        city: partnerForm.city,
        address: partnerForm.address,
        password: partnerForm.password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${partnerForm.partner_type === 'SP' ? 'State Partner' : partnerForm.partner_type === 'DLC' ? 'District Level Center' : 'Training Center'} created successfully!`);
      toast.info(`Center Code: ${response.data.center_code}`, { duration: 10000 });
      
      setAddPartnerDialogOpen(false);
      setPartnerForm({
        partner_type: 'TC', center_name: '', owner_name: '', email: '', mobile: '',
        state: '', district: '', city: '', address: '', password: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create partner');
    } finally {
      setCreatingPartner(false);
    }
  };

  // RBAC Permission Check Helper
  const hasPermission = (module, action = 'view') => {
    if (!isSubAdmin) return true; // Super Admin has all permissions
    
    // Map frontend tab names to backend permission modules
    const moduleMapping = {
      'franchise': 'centers',  // franchise tab uses centers permissions
      'students': 'students',
      'courses': 'courses',
      'placements': 'jobs',    // placements tab uses jobs permissions  
      'certificates': 'certificates',
      'fees': 'fees',
      'lms': 'courses',        // LMS uses courses permissions
      'exams': 'exams',
      'reports': 'reports',
      'activity_logs': 'reports',  // Activity logs uses reports permissions
      'data_health': 'reports'     // Data health uses reports permissions
    };
    
    const backendModule = moduleMapping[module] || module;
    const modulePerms = subAdminPermissions[backendModule];
    if (!modulePerms) return false;
    return modulePerms[action] === true;
  };

  // Define module mappings for tabs
  const tabPermissionMap = {
    'overview': null, // All can view
    'franchise': 'franchise',
    'students': 'students',
    'courses': 'courses',
    'placements': 'placements',
    'certificates': 'certificates',
    'fees': 'fees',
    'lms': 'lms',
    'exams': 'exams',
    'reports': 'reports',
    'roles': null, // Super Admin only
    'sub-admins': null, // Super Admin only
    'activity-logs': 'activity_logs',
    'data-health': 'data_health'
  };

  // Certificate functions
  const generateCertNumber = () => {
    const year = new Date().getFullYear();
    const count = certificates.length + 1;
    return `IQRA-${year}-${String(count).padStart(4, '0')}`;
  };

  const handleAddCertificate = async (e) => {
    e.preventDefault();
    try {
      const data = { ...certFormData, cert_number: certFormData.cert_number || generateCertNumber() };
      await axios.post(`${API}/admin/certificates`, data);
      toast.success('Certificate added successfully');
      setIsDialogOpen(false);
      setCertFormData({ name: '', dob: '', cert_number: '', status: 'valid', course: '', issue_date: '', expiry_date: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add certificate');
    }
  };

  const handleEditCertificate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/certificates/${editingCert.id}`, certFormData);
      toast.success('Certificate updated');
      setIsDialogOpen(false);
      setEditingCert(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update certificate');
    }
  };

  const handleDeleteCertificate = async (certId) => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) return;
    try {
      await axios.delete(`${API}/admin/certificates/${certId}`);
      toast.success('Certificate deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete certificate');
    }
  };

  // Franchise functions
  const handleApprove = async (appId) => {
    try {
      const response = await axios.post(`${API}/partner/approve/${appId}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`Stage 1 Approved! Temp ID: ${response.data.temp_id}`, { duration: 5000 });
      toast.info(`Login: ${response.data.email} / ${response.data.temp_password}`, { duration: 8000 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleFinalApprove = async (appId) => {
    if (!window.confirm('This will generate center code and activate the center. Continue?')) return;
    try {
      const response = await axios.put(`${API}/admin/franchise-applications/${appId}/approve`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`Center Activated! Code: ${response.data.center_code}`, { duration: 5000 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate center');
    }
  };

  const handleReject = async (appId) => {
    if (!window.confirm('Are you sure you want to reject this application?')) return;
    try {
      await axios.put(`${API}/admin/franchise-applications/${appId}/reject`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Application rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  // Course functions
  const handleSaveCourse = async (e) => {
    e.preventDefault();
    try {
      if (editingCourse) {
        await axios.put(`${API}/admin/courses/${editingCourse.id}`, courseForm);
        toast.success('Course updated');
      } else {
        await axios.post(`${API}/admin/courses`, { ...courseForm, fee: parseFloat(courseForm.fee) || 0 });
        toast.success('Course added');
      }
      setCourseDialogOpen(false);
      setEditingCourse(null);
      setCourseForm({ name: '', description: '', duration: '', fee: '', course_type: 'paid', delivery_mode: 'offline', category: 'professional', is_active: true, eligibility: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save course');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    try {
      await axios.delete(`${API}/admin/courses/${courseId}`);
      toast.success('Course deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  // CSV Upload functions
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        toast.error('CSV file is empty or invalid');
        return;
      }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const data = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        const obj = {};
        headers.forEach((h, i) => { obj[h] = values[i] || ''; });
        return obj;
      });
      setCsvData(data);
      toast.success(`${data.length} records loaded from CSV`);
    };
    reader.readAsText(file);
  };

  const handleBulkUpload = async () => {
    if (csvData.length === 0) {
      toast.error('No data to upload');
      return;
    }
    setUploading(true);
    try {
      const response = await axios.post(`${API}/admin/certificates/bulk`, csvData);
      setBulkUploadResult(response.data);
      toast.success(`Uploaded ${response.data.created} certificates`);
      fetchData();
    } catch (error) {
      toast.error('Bulk upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Edit Center Handler
  const handleEditCenter = (center) => {
    setSelectedCenter({ ...center });
    setEditCenterDialogOpen(true);
  };

  const handleSaveCenter = async () => {
    if (!selectedCenter) return;
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/centers/${selectedCenter.id}`, selectedCenter, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Center updated successfully');
      setEditCenterDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update center');
    } finally {
      setIsSaving(false);
    }
  };

  // Edit User Handler
  const handleEditUser = (user) => {
    setSelectedUser({ ...user });
    setEditUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}`, selectedUser, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('User updated successfully');
      setEditUserDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete Handlers
  const handleDeleteClick = (item, type) => {
    if (type === 'center') {
      setSelectedCenter(item);
    } else {
      setSelectedUser(item);
    }
    setDeleteType(type);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      if (deleteType === 'center') {
        await axios.delete(`${API}/admin/centers/${selectedCenter.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Center deleted successfully');
      } else {
        await axios.delete(`${API}/admin/users/${selectedUser.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('User deleted successfully');
      }
      setDeleteConfirmOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setIsDeleting(false);
    }
  };

  // Calculate stats
  const totalStudents = allUsers.filter(u => u.role === 'student').length;
  const totalEmployers = allUsers.filter(u => u.role === 'employer').length;
  const totalCenters = franchiseCenters.length;
  const activeCenters = franchiseCenters.filter(c => c.status === 'active').length;
  const activeCourses = courses.filter(c => c.is_active).length;
  const totalRevenue = commissionReport?.total_collected || 0;
  const pendingRevenue = commissionReport?.pending_amount || 0;
  const adminShare = commissionReport?.iqra_share || 0;
  const pendingApps = franchiseApps.filter(a => a.status === 'pending').length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading Admin Panel...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminDashboardWrapper activeTab={activeTab} onTabChange={setActiveTab}>
      <>
        {/* Sub-Admin Role Banner */}
        {isSubAdmin && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-white font-medium">Sub-Admin Mode: {subAdminInfo?.role_name}</p>
                <p className="text-amber-400/80 text-sm">You have limited access based on your role permissions</p>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions Bar */}
        <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="quick-actions">
          {hasPermission('courses', 'add') && (
            <Button onClick={() => setCourseDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" /> Add Course
            </Button>
          )}
          {/* Add Partner Button - For Super Admin and Operations Sub-Admin */}
          {(hasPermission('franchise', 'add') || !isSubAdmin) && (
            <Button onClick={() => setAddPartnerDialogOpen(true)} className="bg-purple-600 hover:bg-purple-700">
              <Building2 className="w-4 h-4 mr-2" /> Add Partner
            </Button>
          )}
          {hasPermission('franchise', 'approve') && (
            <Button onClick={() => handleTabChange('franchise')} variant="outline" className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10">
              <Building2 className="w-4 h-4 mr-2" /> 
              Approve Franchise
              {pendingApps > 0 && <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{pendingApps}</span>}
            </Button>
          )}
          {!isSubAdmin && (
            <Button variant="outline" className="border-purple-500 text-purple-400 hover:bg-purple-500/10">
              <Send className="w-4 h-4 mr-2" /> Send Notification
            </Button>
          )}
          {hasPermission('reports') && (
            <Button onClick={() => handleTabChange('reports')} variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10">
              <BarChart3 className="w-4 h-4 mr-2" /> View Reports
            </Button>
          )}
          <Button onClick={fetchData} variant="ghost" className="text-slate-400 hover:text-white ml-auto">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Global KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-testid="kpi-cards">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-blue-200 text-sm">Total Centers</p>
                <p className="text-3xl font-bold mt-1">{totalCenters}</p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{activeCenters} active</span>
                </div>
              </div>
              <Building2 className="w-8 h-8 text-blue-300" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-emerald-200 text-sm">Total Students</p>
                <p className="text-3xl font-bold mt-1">{totalStudents}</p>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowUpRight className="w-3 h-3" />
                  <span className="text-xs">+12% growth</span>
                </div>
              </div>
              <Users className="w-8 h-8 text-emerald-300" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-purple-200 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold mt-1">₹{(totalRevenue / 100000).toFixed(1)}L</p>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowUpRight className="w-3 h-3" />
                  <span className="text-xs">+8% this month</span>
                </div>
              </div>
              <IndianRupee className="w-8 h-8 text-purple-300" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-amber-100 text-sm">Total Placements</p>
                <p className="text-3xl font-bold mt-1">11,500+</p>
                <div className="flex items-center gap-1 mt-2">
                  <Trophy className="w-3 h-3" />
                  <span className="text-xs">85% rate</span>
                </div>
              </div>
              <Briefcase className="w-8 h-8 text-amber-200" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-cyan-200 text-sm">Active Courses</p>
                <p className="text-3xl font-bold mt-1">{activeCourses}</p>
                <div className="flex items-center gap-1 mt-2">
                  <BookOpen className="w-3 h-3" />
                  <span className="text-xs">{courses.length} total</span>
                </div>
              </div>
              <GraduationCap className="w-8 h-8 text-cyan-300" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-pink-600 to-pink-700 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-pink-200 text-sm">Employers</p>
                <p className="text-3xl font-bold mt-1">{totalEmployers}</p>
                <div className="flex items-center gap-1 mt-2">
                  <Building className="w-3 h-3" />
                  <span className="text-xs">{jobs.length} jobs</span>
                </div>
              </div>
              <Globe className="w-8 h-8 text-pink-300" />
            </div>
          </div>
        </div>

        {/* Revenue & Alerts Row */}
        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          {/* Revenue Control Panel */}
          <div className="lg:col-span-2 bg-slate-800/50 backdrop-blur rounded-2xl p-6" data-testid="revenue-panel">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-400" />
              Revenue Control Panel
            </h3>
            
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-slate-700/50 rounded-xl p-4">
                <p className="text-slate-400 text-sm">Total Platform Revenue</p>
                <p className="text-2xl font-bold text-white mt-1">₹{(totalRevenue / 100000).toFixed(2)}L</p>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                <p className="text-emerald-400 text-sm">Iqra Share</p>
                <p className="text-2xl font-bold text-emerald-400 mt-1">₹{(adminShare / 100000).toFixed(2)}L</p>
                <p className="text-emerald-400/70 text-xs mt-1">60% Diploma/Degree | 30% Courses</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <p className="text-red-400 text-sm">Pending from Centers</p>
                <p className="text-2xl font-bold text-red-400 mt-1">₹{(pendingRevenue / 1000).toFixed(0)}K</p>
              </div>
            </div>

            {/* Center-wise Revenue */}
            <h4 className="text-sm font-medium text-slate-400 mb-3">Center-wise Collection</h4>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {franchiseCenters.slice(0, 5).map(center => (
                <div key={center.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{center.center_name}</p>
                      <p className="text-slate-400 text-xs">{center.center_code}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-400 font-medium">₹{((center.total_revenue || 0) / 1000).toFixed(0)}K</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      center.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {center.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Alerts & Activity */}
          <div className="space-y-6">
            {/* Alerts Panel */}
            {alerts.length > 0 && (
              <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6" data-testid="alerts-panel">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  Critical Alerts
                </h3>
                <div className="space-y-3">
                  {alerts.map((alert, idx) => (
                    <div 
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-xl ${
                        alert.type === 'danger' ? 'bg-red-500/10 border border-red-500/30' : 'bg-amber-500/10 border border-amber-500/30'
                      }`}
                    >
                      <alert.icon className={`w-5 h-5 ${alert.type === 'danger' ? 'text-red-400' : 'text-amber-400'}`} />
                      <span className={`text-sm ${alert.type === 'danger' ? 'text-red-300' : 'text-amber-300'}`}>
                        {alert.message}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activity Feed */}
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6" data-testid="activity-feed">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-400" />
                  Live Activity
                </h3>
                <button onClick={fetchActivityFeed} className="text-blue-400 hover:text-blue-300 transition-colors">
                  <RefreshCw className={`w-4 h-4 ${activityLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="space-y-3">
                {activityLoading ? (
                  <div className="text-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-400 mx-auto" />
                    <p className="text-xs text-slate-500 mt-2">Loading activities...</p>
                  </div>
                ) : activityFeed.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-4">No recent activity</p>
                ) : (
                  activityFeed.map(activity => {
                    const IconComponent = iconMap[activity.icon] || Activity;
                    return (
                      <div key={activity.id} className="flex items-start gap-3 p-2">
                        <div className={`p-2 rounded-lg ${
                          activity.color === 'blue' ? 'bg-blue-500/20' :
                          activity.color === 'emerald' ? 'bg-emerald-500/20' :
                          activity.color === 'green' ? 'bg-green-500/20' :
                          activity.color === 'purple' ? 'bg-purple-500/20' :
                          activity.color === 'amber' ? 'bg-amber-500/20' :
                          'bg-slate-500/20'
                        }`}>
                          <IconComponent className={`w-4 h-4 ${
                            activity.color === 'blue' ? 'text-blue-400' :
                            activity.color === 'emerald' ? 'text-emerald-400' :
                            activity.color === 'green' ? 'text-green-400' :
                            activity.color === 'purple' ? 'text-purple-400' :
                            activity.color === 'amber' ? 'text-amber-400' :
                            'text-slate-400'
                          }`} />
                        </div>
                        <div>
                          <p className="text-sm text-slate-200">{activity.message}</p>
                          <p className="text-xs text-slate-500">{activity.time}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Tabs */}
        <div ref={tabsSectionRef}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800/50 border border-slate-700 mb-6 p-1 rounded-xl flex-wrap">
            <TabsTrigger value="overview" className="rounded-lg text-slate-400 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Overview
            </TabsTrigger>
            {hasPermission('franchise') && (
              <TabsTrigger value="franchise" className="rounded-lg text-slate-400 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Franchise
                {pendingApps > 0 && <span className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{pendingApps}</span>}
              </TabsTrigger>
            )}
            {hasPermission('students') && (
              <TabsTrigger value="students" className="rounded-lg text-slate-400 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Students
              </TabsTrigger>
            )}
            {hasPermission('courses') && (
              <TabsTrigger value="courses" className="rounded-lg text-slate-400 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Courses
              </TabsTrigger>
            )}
            {hasPermission('placements') && (
              <TabsTrigger value="placements" className="rounded-lg text-slate-400 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Placements
              </TabsTrigger>
            )}
            {hasPermission('certificates') && (
              <TabsTrigger value="certificates" className="rounded-lg text-slate-400 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Certificates
              </TabsTrigger>
            )}
            {hasPermission('fees') && (
              <TabsTrigger value="fees" className="rounded-lg text-slate-400 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Fees
              </TabsTrigger>
            )}
            {hasPermission('lms') && (
              <TabsTrigger value="lms" className="rounded-lg text-slate-400 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                LMS
              </TabsTrigger>
            )}
            {hasPermission('exams') && (
              <TabsTrigger value="exams" className="rounded-lg text-slate-400 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Exams
              </TabsTrigger>
            )}
            {hasPermission('reports') && (
              <TabsTrigger value="reports" className="rounded-lg text-slate-400 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                Reports
              </TabsTrigger>
            )}
            {/* Super Admin Only Tabs */}
            {!isSubAdmin && (
              <>
                <TabsTrigger value="roles" className="rounded-lg text-slate-400 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                  Roles
                </TabsTrigger>
                <TabsTrigger value="sub-admins" className="rounded-lg text-slate-400 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                  Sub-Admins
                </TabsTrigger>
              </>
            )}
            {hasPermission('activity_logs') && (
              <TabsTrigger value="activity-logs" className="rounded-lg text-slate-400 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
                Activity Logs
              </TabsTrigger>
            )}
            {hasPermission('data_health') && (
              <TabsTrigger value="data-health" className="rounded-lg text-slate-400 data-[state=active]:bg-cyan-600 data-[state=active]:text-white" data-testid="admin-tab-data-health">
                Data Health
              </TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Franchise Applications */}
              <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-emerald-400" />
                    Recent Franchise Applications
                  </h3>
                  <Button size="sm" variant="ghost" onClick={() => setActiveTab('franchise')} className="text-blue-400">
                    View All
                  </Button>
                </div>
                <div className="space-y-3">
                  {franchiseApps.slice(0, 5).map(app => (
                    <div key={app.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-xl">
                      <div>
                        <p className="text-white font-medium">{app.center_name}</p>
                        <p className="text-slate-400 text-sm">{app.city}, {app.state}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        app.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                        app.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {app.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Students */}
              <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    Recent Students
                  </h3>
                  <Button size="sm" variant="ghost" onClick={() => setActiveTab('students')} className="text-blue-400">
                    View All
                  </Button>
                </div>
                <div className="space-y-3">
                  {allUsers.filter(u => u.role === 'student').slice(0, 5).map(student => (
                    <div key={student.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                          <span className="text-blue-400 font-medium">{student.name?.charAt(0) || 'S'}</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{student.name}</p>
                          <p className="text-slate-400 text-sm">{student.email}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Franchise Management Tab */}
          <TabsContent value="franchise">
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-emerald-400" />
                  Franchise Management
                </h3>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm">
                    {pendingApps} Pending
                  </span>
                  <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm">
                    {activeCenters} Active
                  </span>
                </div>
              </div>

              {/* Pending Applications */}
              {franchiseApps.filter(a => ['pending', 'stage1_approved', 'payment_pending'].includes(a.status)).length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-amber-400 mb-3">Applications Queue</h4>
                  <div className="space-y-3">
                    {franchiseApps.filter(a => ['pending', 'stage1_approved', 'payment_pending'].includes(a.status)).map(app => (
                      <div key={app.id} className={`rounded-xl p-4 ${
                        app.status === 'pending' ? 'bg-amber-500/10 border border-amber-500/30' :
                        app.status === 'stage1_approved' ? 'bg-blue-500/10 border border-blue-500/30' :
                        'bg-purple-500/10 border border-purple-500/30'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-white">{app.center_name || `${app.owner_name}'s Center`}</h4>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                app.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                app.status === 'stage1_approved' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-purple-500/20 text-purple-400'
                              }`}>
                                {app.status === 'pending' ? 'Pending Review' :
                                 app.status === 'stage1_approved' ? 'Stage 1 Approved' :
                                 'Payment Pending'}
                              </span>
                            </div>
                            <p className="text-slate-400 text-sm">{app.owner_name || app.spoc_name} • {app.mobile}</p>
                            <p className="text-slate-400 text-sm">{app.city}, {app.district}, {app.state}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                                {app.partner_type === 'SP' ? 'State Partner' : 
                                 app.partner_type === 'DLC' ? 'District Partner' : 'Training Center'}
                              </span>
                              <span className="text-emerald-400 text-sm">
                                Fee: ₹{((app.fee || PARTNER_FEES[app.partner_type] || 50000) / 1000).toFixed(0)}K
                              </span>
                              {app.temp_id && (
                                <span className="text-purple-400 text-xs font-mono">
                                  ID: {app.temp_id}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            {app.status === 'pending' && (
                              <>
                                <Button onClick={() => handleApprove(app.id)} size="sm" className="bg-blue-600 hover:bg-blue-700">
                                  <Check className="w-4 h-4 mr-1" /> Stage 1
                                </Button>
                                <Button onClick={() => handleReject(app.id)} size="sm" variant="destructive">
                                  <X className="w-4 h-4 mr-1" /> Reject
                                </Button>
                              </>
                            )}
                            {app.status === 'stage1_approved' && (
                              <Button onClick={() => handleFinalApprove(app.id)} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                                <BadgeCheck className="w-4 h-4 mr-1" /> Activate
                              </Button>
                            )}
                            {app.status === 'payment_pending' && (
                              <Button onClick={() => handleFinalApprove(app.id)} size="sm" className="bg-green-600 hover:bg-green-700">
                                <CreditCard className="w-4 h-4 mr-1" /> Finalize
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Active Centers */}
              <h4 className="text-sm font-medium text-slate-400 mb-3">All Centers</h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Center</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Code</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Type</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Location</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Students</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Revenue</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {franchiseCenters.map(center => (
                      <tr key={center.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-3 px-4">
                          <p className="text-white font-medium">{center.center_name}</p>
                          <p className="text-slate-400 text-xs">{center.owner_name}</p>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-mono text-blue-400">{center.center_code}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">
                            {center.partner_type}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-slate-300">{center.city}, {center.state}</td>
                        <td className="py-3 px-4 text-white font-medium">{center.student_count || 0}</td>
                        <td className="py-3 px-4 text-emerald-400">₹{((center.total_revenue || 0) / 1000).toFixed(0)}K</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            center.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                            center.status === 'blocked' ? 'bg-red-500/20 text-red-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {center.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditCenter(center)}
                              className="p-1.5 hover:bg-slate-600 rounded-lg transition-colors"
                              title="Edit Center"
                            >
                              <Edit className="w-4 h-4 text-blue-400" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(center, 'center')}
                              className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors"
                              title="Delete Center"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-400" />
                  Student Management ({totalStudents} total)
                </h3>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search students..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-700 border-slate-600 text-white rounded-xl"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Student</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Phone</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Joined</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers
                      .filter(u => u.role === 'student')
                      .filter(u => !searchTerm || u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || u.email?.includes(searchTerm))
                      .map(student => (
                        <tr key={student.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                                <span className="text-blue-400 font-medium">{student.name?.charAt(0) || 'S'}</span>
                              </div>
                              <span className="text-white font-medium">{student.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-slate-300">{student.email}</td>
                          <td className="py-3 px-4 text-slate-300">{student.phone || '-'}</td>
                          <td className="py-3 px-4 text-slate-400 text-sm">
                            {student.created_at ? new Date(student.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              <Button size="sm" variant="ghost" className="text-blue-400 p-1">
                                <Eye className="w-4 h-4" />
                              </Button>
                              <button
                                onClick={() => handleEditUser(student)}
                                className="p-1.5 hover:bg-slate-600 rounded-lg transition-colors"
                                title="Edit Student"
                              >
                                <Edit className="w-4 h-4 text-amber-400" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(student, 'user')}
                                className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors"
                                title="Delete Student"
                              >
                                <Trash2 className="w-4 h-4 text-red-400" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses">
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-cyan-400" />
                  Course Management
                </h3>
                <Button onClick={() => { setEditingCourse(null); setCourseDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" /> Add Course
                </Button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {courses.map(course => (
                  <div key={course.id} className="bg-slate-700/50 rounded-xl p-4 border border-slate-600">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-white">{course.name}</h4>
                        <p className="text-slate-400 text-sm">{course.category}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        course.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'
                      }`}>
                        {course.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Duration:</span>
                        <span className="text-white">{course.duration}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Fee:</span>
                        <span className="text-emerald-400">₹{course.fee?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Mode:</span>
                        <span className="text-white capitalize">{course.delivery_mode}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1 border-slate-600 text-slate-300"
                        onClick={() => { setEditingCourse(course); setCourseForm(course); setCourseDialogOpen(true); }}
                      >
                        <Edit className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleDeleteCourse(course.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Placements Tab */}
          <TabsContent value="placements">
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6">
              <h3 className="font-semibold text-white mb-6 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-amber-400" />
                Placement & Job Control
              </h3>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                  <p className="text-slate-400">Total Jobs Posted</p>
                  <p className="text-3xl font-bold text-white mt-2">{jobs.length}</p>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
                  <p className="text-emerald-400">Total Placements</p>
                  <p className="text-3xl font-bold text-emerald-400 mt-2">11,500+</p>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
                  <p className="text-blue-400">Partner Companies</p>
                  <p className="text-3xl font-bold text-blue-400 mt-2">{totalEmployers}</p>
                </div>
              </div>

              <h4 className="text-sm font-medium text-slate-400 mb-3">Recent Jobs</h4>
              <div className="space-y-3">
                {jobs.slice(0, 5).map(job => (
                  <div key={job.id} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                        <Building className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <h4 className="font-medium text-white">{job.title}</h4>
                        <p className="text-slate-400 text-sm">{job.company_name} • {job.location}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-emerald-400 font-medium">{job.salary_range}</p>
                      <span className="text-slate-400 text-xs">{job.applications_count || 0} applications</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Employers List */}
              <h4 className="text-sm font-medium text-slate-400 mb-3 mt-8">All Employers</h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Employer</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Phone</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Joined</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers
                      .filter(u => u.role === 'employer')
                      .map(employer => (
                        <tr key={employer.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-pink-500/20 rounded-full flex items-center justify-center">
                                <span className="text-pink-400 font-medium">{employer.name?.charAt(0) || 'E'}</span>
                              </div>
                              <span className="text-white font-medium">{employer.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-slate-300">{employer.email}</td>
                          <td className="py-3 px-4 text-slate-300">{employer.phone || '-'}</td>
                          <td className="py-3 px-4 text-slate-400 text-sm">
                            {employer.created_at ? new Date(employer.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleEditUser(employer)}
                                className="p-1.5 hover:bg-slate-600 rounded-lg transition-colors"
                                title="Edit Employer"
                              >
                                <Edit className="w-4 h-4 text-amber-400" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(employer, 'user')}
                                className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors"
                                title="Delete Employer"
                              >
                                <Trash2 className="w-4 h-4 text-red-400" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Certificates Tab */}
          <TabsContent value="certificates">
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-400" />
                  Certificate Management
                </h3>
                <div className="flex gap-2">
                  <Button onClick={() => setBulkUploadOpen(true)} variant="outline" className="border-slate-600 text-slate-300">
                    <Upload className="w-4 h-4 mr-2" /> Bulk Upload
                  </Button>
                  <Button onClick={() => { setEditingCert(null); setIsDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" /> Add Certificate
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Name</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Cert Number</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Course</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Issue Date</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {certificates.slice(0, 20).map(cert => (
                      <tr key={cert.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-3 px-4 text-white">{cert.name}</td>
                        <td className="py-3 px-4 font-mono text-blue-400">{cert.cert_number}</td>
                        <td className="py-3 px-4 text-slate-300">{cert.course}</td>
                        <td className="py-3 px-4 text-slate-400">{cert.issue_date}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            cert.status === 'valid' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {cert.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-blue-400"
                              onClick={() => { setEditingCert(cert); setCertFormData(cert); setIsDialogOpen(true); }}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeleteCertificate(cert.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Fees Tab */}
          <TabsContent value="fees">
            <FeeManagement />
          </TabsContent>

          {/* LMS Tab */}
          <TabsContent value="lms">
            <LMSManagement />
          </TabsContent>

          {/* Exams Tab */}
          <TabsContent value="exams">
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-400" />
                  Question Bank Management
                </h3>
                <button 
                  onClick={() => navigate('/admin/question-bank')}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  Open Question Bank
                </button>
              </div>
              <p className="text-slate-400 text-sm">
                Manage bilingual questions (Hindi & English) for all professional courses. 
                Add, edit, delete questions and track question bank status.
              </p>
            </div>
            <ExamManagement />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6">
              <h3 className="font-semibold text-white mb-6 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-400" />
                Reports & Analytics
              </h3>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <button className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-left hover:bg-blue-500/20 transition-colors">
                  <IndianRupee className="w-8 h-8 text-blue-400 mb-2" />
                  <h4 className="font-semibold text-white">Revenue Report</h4>
                  <p className="text-slate-400 text-sm">View income analytics</p>
                </button>
                <button className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-left hover:bg-emerald-500/20 transition-colors">
                  <Building2 className="w-8 h-8 text-emerald-400 mb-2" />
                  <h4 className="font-semibold text-white">Center Performance</h4>
                  <p className="text-slate-400 text-sm">Track franchise stats</p>
                </button>
                <button className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 text-left hover:bg-purple-500/20 transition-colors">
                  <Users className="w-8 h-8 text-purple-400 mb-2" />
                  <h4 className="font-semibold text-white">Student Growth</h4>
                  <p className="text-slate-400 text-sm">Enrollment trends</p>
                </button>
                <button className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-left hover:bg-amber-500/20 transition-colors">
                  <Briefcase className="w-8 h-8 text-amber-400 mb-2" />
                  <h4 className="font-semibold text-white">Placement Report</h4>
                  <p className="text-slate-400 text-sm">Job & hiring data</p>
                </button>
              </div>

              {/* Commission Report */}
              {commissionReport && (
                <div className="bg-slate-700/30 rounded-xl p-6">
                  <h4 className="font-semibold text-white mb-2">Commission Summary</h4>
                  <p className="text-slate-400 text-xs mb-4">Diploma & Degree: Iqra 60% | Professional Courses: Iqra 30%</p>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-slate-400 text-sm">Total Collected</p>
                      <p className="text-2xl font-bold text-white">₹{(commissionReport.total_collected / 100000).toFixed(2)}L</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Iqra Share</p>
                      <p className="text-2xl font-bold text-emerald-400">₹{(commissionReport.iqra_share / 100000).toFixed(2)}L</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Center Share</p>
                      <p className="text-2xl font-bold text-blue-400">₹{(commissionReport.center_share / 100000).toFixed(2)}L</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Pending</p>
                      <p className="text-2xl font-bold text-red-400">₹{(commissionReport.pending_amount / 1000).toFixed(0)}K</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Roles Tab */}
          <TabsContent value="roles">
            <RoleManagement />
          </TabsContent>

          {/* Sub-Admins Tab */}
          <TabsContent value="sub-admins">
            <SubAdminManagement />
          </TabsContent>

          {/* Activity Logs Tab */}
          <TabsContent value="activity-logs">
            <ActivityLogs />
          </TabsContent>

          {/* Data Health Tab */}
          <TabsContent value="data-health">
            <DataHealth />
          </TabsContent>
        </Tabs>
        </div>

      {/* Course Dialog */}
      <Dialog open={courseDialogOpen} onOpenChange={setCourseDialogOpen}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">{editingCourse ? 'Edit Course' : 'Add New Course'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCourse} className="space-y-4">
            <div>
              <Label className="text-slate-300">Course Name</Label>
              <Input value={courseForm.name} onChange={(e) => setCourseForm({...courseForm, name: e.target.value})} required className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Duration</Label>
                <Input value={courseForm.duration} onChange={(e) => setCourseForm({...courseForm, duration: e.target.value})} placeholder="e.g., 3 months" className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div>
                <Label className="text-slate-300">Fee (₹)</Label>
                <Input type="number" value={courseForm.fee} onChange={(e) => setCourseForm({...courseForm, fee: e.target.value})} className="bg-slate-700 border-slate-600 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Category</Label>
                <select value={courseForm.category} onChange={(e) => setCourseForm({...courseForm, category: e.target.value})} className="w-full h-10 px-3 bg-slate-700 border border-slate-600 rounded-md text-white">
                  <option value="professional">Professional</option>
                  <option value="distance_learning">Distance Learning</option>
                  <option value="government">Government</option>
                </select>
              </div>
              <div>
                <Label className="text-slate-300">Delivery Mode</Label>
                <select value={courseForm.delivery_mode} onChange={(e) => setCourseForm({...courseForm, delivery_mode: e.target.value})} className="w-full h-10 px-3 bg-slate-700 border border-slate-600 rounded-md text-white">
                  <option value="offline">Offline</option>
                  <option value="online">Online</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Description</Label>
              <Input value={courseForm.description} onChange={(e) => setCourseForm({...courseForm, description: e.target.value})} className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setCourseDialogOpen(false)} className="border-slate-600 text-slate-300">Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">{editingCourse ? 'Update' : 'Add'} Course</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Certificate Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">{editingCert ? 'Edit Certificate' : 'Add Certificate'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={editingCert ? handleEditCertificate : handleAddCertificate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Name</Label>
                <Input value={certFormData.name} onChange={(e) => setCertFormData({...certFormData, name: e.target.value})} required className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div>
                <Label className="text-slate-300">DOB</Label>
                <Input type="date" value={certFormData.dob} onChange={(e) => setCertFormData({...certFormData, dob: e.target.value})} className="bg-slate-700 border-slate-600 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Course</Label>
                <Input value={certFormData.course} onChange={(e) => setCertFormData({...certFormData, course: e.target.value})} className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div>
                <Label className="text-slate-300">Cert Number</Label>
                <Input value={certFormData.cert_number} onChange={(e) => setCertFormData({...certFormData, cert_number: e.target.value})} placeholder="Auto-generated" className="bg-slate-700 border-slate-600 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Issue Date</Label>
                <Input type="date" value={certFormData.issue_date} onChange={(e) => setCertFormData({...certFormData, issue_date: e.target.value})} className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div>
                <Label className="text-slate-300">Status</Label>
                <select value={certFormData.status} onChange={(e) => setCertFormData({...certFormData, status: e.target.value})} className="w-full h-10 px-3 bg-slate-700 border border-slate-600 rounded-md text-white">
                  <option value="valid">Valid</option>
                  <option value="revoked">Revoked</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-slate-600 text-slate-300">Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">{editingCert ? 'Update' : 'Add'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkUploadOpen} onOpenChange={setBulkUploadOpen}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Bulk Upload Certificates</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-600 rounded-xl p-6 text-center">
              <Upload className="w-12 h-12 text-slate-400 mx-auto mb-2" />
              <p className="text-slate-300">Upload CSV file</p>
              <p className="text-slate-500 text-sm mb-3">Columns: name, dob, course, issue_date</p>
              <input type="file" accept=".csv" onChange={handleFileUpload} ref={fileInputRef} className="hidden" />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="border-slate-600 text-slate-300">
                Select File
              </Button>
            </div>
            {csvData.length > 0 && (
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-emerald-400">{csvData.length} records ready to upload</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkUploadOpen(false)} className="border-slate-600 text-slate-300">Cancel</Button>
              <Button onClick={handleBulkUpload} disabled={csvData.length === 0 || uploading} className="bg-blue-600 hover:bg-blue-700">
                {uploading ? 'Uploading...' : 'Upload All'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Partner Dialog */}
      <Dialog open={addPartnerDialogOpen} onOpenChange={setAddPartnerDialogOpen}>
        <DialogContent className="max-w-xl bg-slate-800 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-400" />
              Add Partner / Training Center
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4 pb-4">
            {/* Partner Type Selection */}
            <div>
              <Label className="text-slate-300">Partner Type *</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[
                  { id: 'SP', label: 'State Partner', fee: '₹5,00,000', color: 'amber' },
                  { id: 'DLC', label: 'District Level Center', fee: '₹1,50,000', color: 'purple' },
                  { id: 'TC', label: 'Training Center', fee: '₹50,000', color: 'blue' }
                ].map(type => (
                  <button
                    key={type.id}
                    onClick={() => setPartnerForm({ ...partnerForm, partner_type: type.id })}
                    className={`p-3 rounded-xl border text-center transition-all ${
                      partnerForm.partner_type === type.id 
                        ? `bg-${type.color}-500/20 border-${type.color}-500 text-${type.color}-400` 
                        : 'border-slate-600 text-slate-400 hover:border-slate-500'
                    }`}
                  >
                    <p className="font-medium text-sm">{type.label}</p>
                    <p className="text-xs mt-1 opacity-70">{type.fee}</p>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Center Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Center Name *</Label>
                <Input
                  value={partnerForm.center_name}
                  onChange={(e) => setPartnerForm({ ...partnerForm, center_name: e.target.value })}
                  placeholder="Training center name"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">SPOC Name *</Label>
                <Input
                  value={partnerForm.owner_name}
                  onChange={(e) => setPartnerForm({ ...partnerForm, owner_name: e.target.value })}
                  placeholder="SPOC name"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Email *</Label>
                <Input
                  type="email"
                  value={partnerForm.email}
                  onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })}
                  placeholder="email@example.com"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Mobile *</Label>
                <Input
                  value={partnerForm.mobile}
                  onChange={(e) => setPartnerForm({ ...partnerForm, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  placeholder="10-digit mobile"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              {/* State - Required for SP and DLC */}
              <div>
                <Label className="text-slate-300">
                  State {(partnerForm.partner_type === 'SP' || partnerForm.partner_type === 'DLC') && '*'}
                </Label>
                <select
                  value={partnerForm.state}
                  onChange={(e) => setPartnerForm({ ...partnerForm, state: e.target.value, district: '' })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select State</option>
                  {INDIAN_STATES.map((state, idx) => (
                    <option key={idx} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              
              {/* District - Required for DLC */}
              <div>
                <Label className="text-slate-300">
                  District {partnerForm.partner_type === 'DLC' && '*'}
                </Label>
                <select
                  value={partnerForm.district}
                  onChange={(e) => setPartnerForm({ ...partnerForm, district: e.target.value })}
                  disabled={!partnerForm.state || loadingDistricts}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                >
                  <option value="">{loadingDistricts ? 'Loading...' : 'Select District'}</option>
                  {partnerDistricts.map((district, idx) => (
                    <option key={idx} value={district}>{district}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label className="text-slate-300">City</Label>
                <Input
                  value={partnerForm.city}
                  onChange={(e) => setPartnerForm({ ...partnerForm, city: e.target.value })}
                  placeholder="City"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">PIN Code</Label>
                <Input
                  value={partnerForm.pin_code}
                  onChange={(e) => setPartnerForm({ ...partnerForm, pin_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  placeholder="6-digit PIN"
                  maxLength={6}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Password *</Label>
                <Input
                  type="password"
                  value={partnerForm.password}
                  onChange={(e) => setPartnerForm({ ...partnerForm, password: e.target.value })}
                  placeholder="Login password"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              <div className="col-span-2">
                <Label className="text-slate-300">Address</Label>
                <Input
                  value={partnerForm.address}
                  onChange={(e) => setPartnerForm({ ...partnerForm, address: e.target.value })}
                  placeholder="Full address"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
            
            {/* Info Box */}
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-3 text-sm">
              <p className="text-purple-300">
                <strong>Note:</strong> Partner will be created directly without payment verification. 
                Center code will be auto-generated.
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setAddPartnerDialogOpen(false)}
                className="border-slate-600 text-slate-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreatePartner}
                disabled={creatingPartner}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {creatingPartner ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" /> Create Partner
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Center Dialog */}
      <Dialog open={editCenterDialogOpen} onOpenChange={setEditCenterDialogOpen}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-400" />
              Edit Training Center
            </DialogTitle>
          </DialogHeader>
          
          {selectedCenter && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Center Name</Label>
                  <Input
                    value={selectedCenter.center_name || ''}
                    onChange={(e) => setSelectedCenter({ ...selectedCenter, center_name: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">SPOC Name</Label>
                  <Input
                    value={selectedCenter.owner_name || ''}
                    onChange={(e) => setSelectedCenter({ ...selectedCenter, owner_name: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    value={selectedCenter.email || ''}
                    onChange={(e) => setSelectedCenter({ ...selectedCenter, email: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Mobile</Label>
                  <Input
                    value={selectedCenter.mobile || ''}
                    onChange={(e) => setSelectedCenter({ ...selectedCenter, mobile: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">State</Label>
                  <Input
                    value={selectedCenter.state || ''}
                    onChange={(e) => setSelectedCenter({ ...selectedCenter, state: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">District</Label>
                  <Input
                    value={selectedCenter.district || ''}
                    onChange={(e) => setSelectedCenter({ ...selectedCenter, district: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">City</Label>
                  <Input
                    value={selectedCenter.city || ''}
                    onChange={(e) => setSelectedCenter({ ...selectedCenter, city: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Status</Label>
                  <select
                    value={selectedCenter.status || 'active'}
                    onChange={(e) => setSelectedCenter({ ...selectedCenter, status: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 text-white rounded-md"
                  >
                    <option value="active">Active</option>
                    <option value="blocked">Blocked</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Address</Label>
                <Input
                  value={selectedCenter.address || ''}
                  onChange={(e) => setSelectedCenter({ ...selectedCenter, address: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditCenterDialogOpen(false)} className="border-slate-600 text-slate-300">
                  Cancel
                </Button>
                <Button onClick={handleSaveCenter} disabled={isSaving} className="bg-amber-600 hover:bg-amber-700">
                  {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-400" />
              Edit {selectedUser?.role === 'student' ? 'Student' : 'Employer'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-300">Name</Label>
                <Input
                  value={selectedUser.name || ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input
                  value={selectedUser.email || ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Phone</Label>
                <Input
                  value={selectedUser.phone || ''}
                  onChange={(e) => setSelectedUser({ ...selectedUser, phone: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditUserDialogOpen(false)} className="border-slate-600 text-slate-300">
                  Cancel
                </Button>
                <Button onClick={handleSaveUser} disabled={isSaving} className="bg-amber-600 hover:bg-amber-700">
                  {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-slate-300">
              Are you sure you want to delete this {deleteType === 'center' ? 'training center' : 'user'}?
            </p>
            <p className="text-red-400 text-sm mt-2">
              {deleteType === 'center' 
                ? `Center: ${selectedCenter?.center_name}` 
                : `User: ${selectedUser?.name}`}
            </p>
            <p className="text-slate-400 text-xs mt-2">This action cannot be undone.</p>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} disabled={isDeleting} className="bg-red-600 hover:bg-red-700">
              {isDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </>
    </AdminDashboardWrapper>
  );
};

export default AdminDashboard;
